import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import HeroDiaporama from './HeroDiaporama'
import { DIAPORAMA_ACCUEIL } from '../data/visuels'
import { NOMBRE_DE_MAGASINS, VILLES, HORAIRES } from '../data/magasins'
import './Hero.css'

/**
 * L'ouverture de l'accueil.
 *
 * Ce qu'elle etait : un carrousel de trois photos Unsplash, quatre chiffres
 * ecrits a la main (« 10+ magasins » pour neuf reels, « 5K+ clients »,
 * « 500+ produits » pour quatre en base, « 3 villes »), une pastille
 * « 4.9/5 sur 2 400+ avis » alors que la table des avis est vide, et un
 * bouton « Voir la video » sans gestionnaire de clic. La premiere chose que
 * voyait un visiteur, donc, etait un decor emprunte legende de chiffres
 * inventes.
 *
 * Ce qu'elle est : un bloc de texte et un diaporama des photographies
 * d'ABBUSINESS. Les trois faits affiches sont verifiables, et deux d'entre
 * eux sont derives de la liste des magasins plutot que recopies : ouvrir une
 * boutique met le chiffre a jour, ce qui est le seul moyen connu de ne pas
 * le laisser mentir.
 *
 * Une etape intermediaire y montrait une photo de produit tiree du
 * catalogue. Elle a fait son office — sortir les trois photos de banque
 * d'images du carrousel d'origine — mais sa place est dans la mise en avant
 * produit, juste en dessous, ou la meme photo reapparaissait. L'ouverture
 * doit montrer la maison, pas repeter le rayon.
 *
 * Tant qu'aucune photo n'est declaree dans DIAPORAMA_ACCUEIL, la colonne
 * visuelle disparait et le texte occupe la largeur. C'est un etat prevu :
 * mieux vaut une ouverture sobre qu'un cadre vide, et beaucoup mieux qu'une
 * image empruntee.
 */

const TEXTES = {
  fr: {
    surtitre: VILLES.join(' · '),
    titre1: 'Tout pour la maison,',
    titre2: 'au bon prix.',
    accroche:
      'Mobilier, électroménager et décoration. Livraison à domicile, paiement à la réception de votre commande.',
    catalogue: 'Voir le catalogue',
    magasins: 'Trouver un magasin',
    faits: [
      { valeur: `${NOMBRE_DE_MAGASINS} magasins`, precision: VILLES.join(', ') },
      { valeur: 'Paiement à la réception', precision: 'ou par Orange Money' },
      { valeur: HORAIRES.fr, precision: 'fermé le dimanche' },
    ],
  },
  en: {
    surtitre: VILLES.join(' · '),
    titre1: 'Everything for the home,',
    titre2: 'at the right price.',
    accroche:
      'Furniture, appliances and interior decoration. Home delivery, payment when your order arrives.',
    catalogue: 'Browse the catalog',
    magasins: 'Find a store',
    faits: [
      { valeur: `${NOMBRE_DE_MAGASINS} stores`, precision: VILLES.join(', ') },
      { valeur: 'Pay on delivery', precision: 'or with Orange Money' },
      { valeur: HORAIRES.en, precision: 'closed on Sundays' },
    ],
  },
}

export default function Hero({ lang }) {
  const texte = TEXTES[lang] ?? TEXTES.fr
  const avecPhotos = DIAPORAMA_ACCUEIL.length > 0

  return (
    <section className="hero" id="hero">
      <div className={`hero-inner${avecPhotos ? '' : ' hero-inner-solo'}`}>
        <div className="hero-lede">
          <p className="hero-eyebrow">{texte.surtitre}</p>

          <h1 className="hero-title">
            {texte.titre1}
            <br />
            <span className="hero-title-accent">{texte.titre2}</span>
          </h1>

          <p className="hero-lead">{texte.accroche}</p>

          <div className="hero-actions">
            <Link to="/catalogue" className="btn btn-primary hero-cta">
              {texte.catalogue}
              <ArrowRight size={17} />
            </Link>
            <a href="#stores" className="hero-secondary">
              {texte.magasins}
            </a>
          </div>

          <dl className="hero-facts">
            {texte.faits.map((fait) => (
              <div className="hero-fact" key={fait.valeur}>
                <dt className="hero-fact-value">{fait.valeur}</dt>
                <dd className="hero-fact-note">{fait.precision}</dd>
              </div>
            ))}
          </dl>
        </div>

        <HeroDiaporama photos={DIAPORAMA_ACCUEIL} lang={lang} />

      </div>
    </section>
  )
}
